import { IconName } from '@fortawesome/fontawesome-svg-core';
import { SimplifiedSearchMode } from 'devextreme/common';

// This file is for types shared by multiple @smart-builder modules.
// Separated into a separate module (@smart-builder/types) so that other modules can use these types without having
//    to import entire modules

export type CROption = {
  identifier: any;

  title: string;
  description?: string;
  hint?: string;
  imageURL?: string;
  scene?: any;
  rgb?: number;
  category?: string;

  data?: any;

  disabled?: boolean; // Only for comboboxes for now.
};

export enum FieldType {
  compound = -100, // Special type. Cannot be passed into a CRControl
  option = 0, // Real field types start from 0
  text,
  memo,
  integer,
  float,
  currency,
  length,
  lengthF,
  phone,
  email,
  colour,
  date,
  time,
  dateTime,
  boolean,
  image,
  percent,
  password,
  richtext,
  font,
}

export type ControlFieldType = Exclude<FieldType, FieldType.compound>;

export enum OptionType {
  // Make sure when adding new OptionTypes, to also update SimpleBooleanType
  cards,
  button,
  combobox,
  list, // Radio or Checkboxes.
  grid, // Each option has 3 column. Name, Description (hint), and Image.
}

export enum SimpleBooleanType {
  // These needs to offset from after OptionType. See type BooleanType
  switch = 5,
  checkbox,
  lock,
}

export type BooleanType = OptionType | SimpleBooleanType;

export type FieldDefn = {
  fieldType: FieldType;

  title: string;
  hint?: string; // default ""
  prompt?: string; // default undefined

  required?: boolean; // default false
  readOnly?: boolean; // default false
  visible?: boolean; // default true

  resetSubsequentQuestionsOnNewValue?: boolean; // Default false. For subFields of compound fields only.

  // option. Applicable to all other question types too,
  // in which case the options will be prepended in the UI. // TODO
  options?: CROption[];
};
export type FieldDefnCompound = FieldDefn & {
  fieldType: FieldType.compound;

  fields: CRField[];
};
export type FieldDefnOption = FieldDefn & {
  fieldType: FieldType.option;
  isMultiSelect?: boolean; // default false
};
export type FieldDefnText = FieldDefn & {
  fieldType:
    | FieldType.text
    | FieldType.memo
    | FieldType.phone
    | FieldType.email
    | FieldType.password;
  minLength?: number;
  maxLength?: number;
  regex?: string; // for phone, this is replacement. Everything else is in addition.

  // default to off when autocomplete is undefined
  // Note: Since Chrome ignores autocomplete=off (https://bugs.chromium.org/p/chromium/issues/detail?id=468153#c164),
  //    we actually set it to autocomplete=unknown
  autocomplete?:
    | 'on'
    | 'new-password'
    | 'current-password'
    | 'email'
    | 'username';
};
export type FieldDefnNumericBase = FieldDefn & {
  fieldType:
    | FieldType.integer
    | FieldType.float
    | FieldType.percent
    | FieldType.currency
    | FieldType.length
    | FieldType.lengthF;
  allowNegative?: boolean; // Default False
};
export type FieldDefnNumeric = FieldDefnNumericBase & {
  fieldType: FieldType.integer | FieldType.float | FieldType.percent;
  minValue?: number;
  maxValue?: number;
  spinStep?: number; // default 1
  spinDirection?: 'horizontal' | 'vertical' | 'rotate'; // default vertical
};
export type FieldDefnCurrency = FieldDefnNumericBase & {
  fieldType: FieldType.currency;
};
export type FieldDefnLength = FieldDefnNumericBase & {
  fieldType: FieldType.length | FieldType.lengthF;
};
export type FieldDefnColour = FieldDefn & {
  fieldType: FieldType.colour;
};
export type FieldDefnFont = FieldDefn & {
  fieldType: FieldType.font;
};
export type FieldDefnDateTime = FieldDefn & {
  fieldType: FieldType.date | FieldType.time | FieldType.dateTime;
};
export type FieldDefnBoolean = FieldDefn & {
  fieldType: FieldType.boolean;
};
export type FieldDefnImage = FieldDefn & {
  fieldType: FieldType.image;
};
export type FieldDefnRichText = FieldDefn & {
  fieldType: FieldType.richtext;
};
export type FieldDefnAny = // FieldTypes Supported by Control

    | FieldDefnOption
    | FieldDefnText
    | FieldDefnCurrency
    | FieldDefnNumeric
    | FieldDefnLength
    | FieldDefnColour
    | FieldDefnDateTime
    | FieldDefnBoolean
    | FieldDefnImage
    | FieldDefnRichText
    | FieldDefnFont;
export type ValidationResult = {
  message: string;
  action: 'warn' | 'trap-focus';
};
export type FieldValue<T = any> = {
  value: T | undefined;
  sequenceNum?: number;
  validationResult?: ValidationResult;
};
export type CRField<T = any> = (FieldDefnAny | FieldDefnCompound) & {
  fieldName: string;

  client: FieldValue<T>;
  server: FieldValue<T>;

  isOverridden?: boolean; // special flag - used only by bulk change edge atm. May be used later for overridden from defaultBO later.
};

export enum UINumericType {
  edit,
  spinEdit,
  slider,
}

// The following should only ever be merged in, rather then returned
// from the server.
export type FieldDisplayProperties = {
  show?: boolean;
  // For Options only
  showOptionTitle?: boolean; // default true
  showOptionImage?: boolean; // default true
  showOptionDescription?: boolean; // default false
  allowOption?: (option: CROption) => boolean; // default undefined (means all options valid)
  allowInlineSvgInOptionImage?: boolean;
  showOptionImageAsBackground?: boolean; // default false
  showWhenSingleOption?: boolean; // default true
  optionType?: OptionType; // default OptionType.cards
  useOptionTitleAsHint?: boolean; // default false
  useCard?: boolean; // default false
  cardClassName?: string;
  cardHeaderClassName?: string;
  cardHeadingType?: 'h4' | 'h5' | 'h6'; // defaults to h5
  onDoubleClickOption?: (option: CROption) => void; // default undefined. Only valid for optionType card and grid.
  showClearButton?: boolean; // default false
  showResetButton?: boolean; // default false. Different from clear for length and numeric fields, as it sets back to 0
  allowSearchCombo?: boolean; // default false
  comboSearchMode?: SimplifiedSearchMode; // default tokenized
  allowSearchCards?: boolean; // default false
  autoSelectOnSearchGrid?: boolean; // default false
  OptionComponent?: React.FC<CROption>;
  dropdownClassName?: string;
  maxDisplayedOptions?: number; // default undefined. Only used by lite currently
  allowSortComboItems?: boolean; // default true

  // For non-option with options
  autoCreateCustomOption?: boolean; // default false

  // For booleans
  booleanType?: BooleanType; // default Switch

  // General
  className?: string;
  autoFocus?: boolean; // default false
  allowFocus?: boolean; // default false for button option type. true for everything else.
  postOnNoChange?: boolean; // default false
  showQuestionTitle?: boolean; // default true
  enabled?: boolean; // default true

  onFocus?: () => void; // only implemented for ControlOptions Button atm

  // For integer, float
  numericType?: UINumericType; // default UINumericType.edit

  // For all text-based edits
  showInputStatusSuccess?: boolean; // default false
  showInputStatusError?: boolean; // default true
  prompt?: string; // default undefined
  postOnTextChange?: boolean; // default false, which only posts on focus out.
  icon?: IconName; // default undefined
  onKeyDown?: (key: string) => void; // default undefined

  // For length fields
  allowLengthKeyboard?: boolean;

  // For multiselect
  showBulkSelectButtons?: boolean; // default false

  // For RTF
  inlineRTFEditor?: boolean; // default false
  onRTFStateChange?: (state: 'dirty' | 'posted') => void;
};
type FieldDefnOverrideFragment<T extends FieldDefn> = Omit<
  Partial<T>,
  'options' | 'fieldType' | 'isMultiSelect'
>;
export type FieldDefnOverride = {
  [key: string]:
    | FieldDefnOverrideFragment<FieldDefnOption>
    | FieldDefnOverrideFragment<FieldDefnText>
    | FieldDefnOverrideFragment<FieldDefnNumeric>
    | FieldDefnOverrideFragment<FieldDefnLength>
    | FieldDefnOverrideFragment<FieldDefnColour>
    | FieldDefnOverrideFragment<FieldDefnDateTime>
    | FieldDefnOverrideFragment<FieldDefnBoolean>
    | FieldDefnOverrideFragment<FieldDefnRichText>;
};

export enum TabDirection {
  none,
  previous,
  next,
}

export type ControlProps = {
  fieldDefn: FieldDefnAny;
  displayProperties: FieldDisplayProperties;
  selectedValue?: any;

  onSelect: (
    value: any,
    // Note that tabDirection is only supported by ControlInput.
    tabDirection?: TabDirection,
  ) => void;
  onChange?: (value: any, isReverting: boolean) => void;

  updating?: boolean;
  validationResult?: ValidationResult;
  resetFromField?: number;
};
export type DelegatedControlProps<T extends FieldDefn> = ControlProps & {
  fieldDefn: T;
  editClassNames: string[];
};

export type PatchData = {
  fieldName: string;

  // SBDialog useEffectDialogOnResponse handles displaying a message with the appropriate severity
  message?: string;
  severity?: number;

  // Handled by the onSuccess of the PATCHer
  result?: string;
}[];

export type CRWarning = {
  id: number;
  severity: number;
  priority: number;
  text: string;
  fieldName?: string;
  immediatePopup?: boolean;
  showOnField?: boolean;
};
export type SelectOptions<TValue, TLabel> = readonly SelectOption<
  TValue,
  TLabel
>[];
export type SelectOption<TValue, TLabel> = {
  value: TValue;
  label: TLabel;
};

export enum DialogAction {
  Ok = 'ok',
  Yes = 'yes',
  YesToAll = 'yesToAll',
  No = 'no',
  NoToAll = 'noToAll',
  Cancel = 'cancel',
}
